/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import type {InviteCandidate} from '@app/components/channel/direct_message/DMListHelpers';
import type {useDMListItemHandlers} from '@app/components/channel/direct_message/useDMListItemHandlers';
import {getMuteDurationOptions} from '@app/components/channel/MuteOptions';
import {ChannelDebugModal} from '@app/components/debug/ChannelDebugModal';
import {UserDebugModal} from '@app/components/debug/UserDebugModal';
import {
	AcceptFriendRequestIcon,
	AddNoteIcon,
	BlockUserIcon,
	CancelFriendRequestIcon,
	CloseDMIcon,
	CopyIdIcon,
	DebugIcon,
	EditGroupIcon,
	EditIcon,
	FavoriteIcon,
	GroupInvitesIcon,
	IgnoreFriendRequestIcon,
	InviteToCommunityIcon,
	LeaveIcon,
	MarkAsReadIcon,
	MuteIcon,
	PinIcon,
	RemoveFriendIcon,
	SendFriendRequestIcon,
	SendInviteToCommunityIcon,
	ViewProfileIcon,
	VoiceCallIcon,
} from '@app/components/uikit/context_menu/ContextMenuIcons';
import type {MenuGroupType, MenuItemType} from '@app/components/uikit/menu_bottom_sheet/MenuBottomSheet';
import type {ChannelRecord} from '@app/records/ChannelRecord';
import type {UserRecord} from '@app/records/UserRecord';
import AccessibilityStore from '@app/stores/AccessibilityStore';
import AuthenticationStore from '@app/stores/AuthenticationStore';
import FavoritesStore from '@app/stores/FavoritesStore';
import UserGuildSettingsStore from '@app/stores/UserGuildSettingsStore';
import UserSettingsStore from '@app/stores/UserSettingsStore';
import UserStore from '@app/stores/UserStore';
import {getMutedText} from '@app/utils/ContextMenuUtils';
import {PublicUserFlags, RelationshipTypes} from '@fluxer/constants/src/UserConstants';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

interface BuildMenuGroupsParams {
	channel: ChannelRecord;
	recipient: UserRecord | null;
	relationshipType: number | undefined;
	restrictRecipientActions: boolean;
	inviteCandidates: Array<InviteCandidate>;
	hasUnreadMessages: boolean;
	handlers: ReturnType<typeof useDMListItemHandlers>;
	openNestedSheet: (title: string, groups: Array<MenuGroupType>) => void;
	closeAllSheets: () => void;
	i18n: I18n;
}

function buildFavoriteGroups(handlers: ReturnType<typeof useDMListItemHandlers>): Array<MenuGroupType> {
	const favoritesCategories = FavoritesStore.sortedCategories;
	return [
		{
			items: [
				{
					icon: <FavoriteIcon size={20} filled={true} />,
					label: 'Uncategorized',
					onClick: () => handlers.handleAddToFavorites(null),
				},
				...favoritesCategories.map((category) => ({
					icon: <FavoriteIcon size={20} filled={true} />,
					label: category.name,
					onClick: () => handlers.handleAddToFavorites(category.id),
				})),
			],
		},
	];
}

function buildInviteGroups(
	inviteCandidates: Array<InviteCandidate>,
	handlers: ReturnType<typeof useDMListItemHandlers>,
): Array<MenuGroupType> {
	return [
		{
			items: inviteCandidates.map((candidate) => ({
				icon: <SendInviteToCommunityIcon size={20} />,
				label: candidate.guild.name,
				onClick: () => handlers.handleSendInvite(candidate),
			})),
		},
	];
}

function buildMuteGroups(i18n: I18n, handlers: ReturnType<typeof useDMListItemHandlers>): Array<MenuGroupType> {
	const muteDurationOptions = getMuteDurationOptions(i18n);
	return [
		{
			items: muteDurationOptions.map((duration) => ({
				label: duration.label,
				onClick: () => handlers.handleMute(duration.value),
			})),
		},
	];
}

export function buildDesktopMenuGroups({
	channel,
	isGroupDM,
	handlers,
	i18n,
}: Pick<BuildMenuGroupsParams, 'channel' | 'handlers' | 'i18n'> & {isGroupDM: boolean}): Array<MenuGroupType> {
	const menuGroups: Array<MenuGroupType> = [];

	if (isGroupDM) {
		menuGroups.push({
			items: [
				{
					icon: <EditGroupIcon size={20} />,
					label: i18n._(msg`Edit Group`),
					onClick: handlers.handleEditGroup,
				},
				channel.isPinned
					? {
							icon: <PinIcon size={20} />,
							label: i18n._(msg`Unpin Group DM`),
							onClick: handlers.handleUnpinChannel,
						}
					: {
							icon: <PinIcon size={20} />,
							label: i18n._(msg`Pin Group DM`),
							onClick: handlers.handlePinChannel,
						},
			],
		});
		const isOwner = channel.ownerId === AuthenticationStore.currentUserId;
		if (isOwner) {
			menuGroups[0].items.push({
				icon: <GroupInvitesIcon size={20} />,
				label: i18n._(msg`Invites`),
				onClick: handlers.handleShowInvites,
			});
		}
		menuGroups.push({
			items: [
				{
					icon: <LeaveIcon size={20} />,
					label: i18n._(msg`Leave Group`),
					onClick: handlers.handleLeaveGroup,
					danger: true,
				},
				{
					icon: <CopyIdIcon size={20} />,
					label: i18n._(msg`Copy Channel ID`),
					onClick: handlers.handleCopyChannelId,
				},
			],
		});
	} else {
		menuGroups.push({
			items: [
				channel.isPinned
					? {
							icon: <PinIcon size={20} />,
							label: i18n._(msg`Unpin DM`),
							onClick: handlers.handleUnpinChannel,
						}
					: {
							icon: <PinIcon size={20} />,
							label: i18n._(msg`Pin DM`),
							onClick: handlers.handlePinChannel,
						},
				{
					icon: <CloseDMIcon size={20} />,
					label: i18n._(msg`Close DM`),
					onClick: () => handlers.handleRemoveChannel(),
					danger: true,
				},
				{
					icon: <CopyIdIcon size={20} />,
					label: i18n._(msg`Copy Channel ID`),
					onClick: handlers.handleCopyChannelId,
				},
			],
		});
	}

	return menuGroups;
}

export function buildMobileMenuGroups({
	channel,
	recipient,
	relationshipType,
	restrictRecipientActions,
	inviteCandidates,
	hasUnreadMessages,
	handlers,
	openNestedSheet,
	closeAllSheets,
	i18n,
}: BuildMenuGroupsParams): Array<MenuGroupType> {
	const mobileMenuGroups: Array<MenuGroupType> = [];

	mobileMenuGroups.push({
		items: [
			{
				icon: <MarkAsReadIcon size={20} />,
				label: i18n._(msg`Mark as Read`),
				onClick: handlers.handleMarkAsRead,
				disabled: !hasUnreadMessages,
			},
			{
				icon: <PinIcon size={20} />,
				label: channel.isPinned ? i18n._(msg`Unpin DM`) : i18n._(msg`Pin DM`),
				onClick: channel.isPinned ? handlers.handleUnpinChannel : handlers.handlePinChannel,
			},
		],
	});

	if (AccessibilityStore.showFavorites) {
		const favoriteEntry = FavoritesStore.getChannel(channel.id);
		const favoritesCategories = FavoritesStore.sortedCategories;
		const favoriteLabel = (() => {
			if (channel.isDM()) {
				return i18n._(msg`Favorite DM`);
			}
			if (channel.isGroupDM()) {
				return i18n._(msg`Favorite Group DM`);
			}
			return i18n._(msg`Favorite Channel`);
		})();
		const unfavoriteLabel = (() => {
			if (channel.isDM()) {
				return i18n._(msg`Unfavorite DM`);
			}
			if (channel.isGroupDM()) {
				return i18n._(msg`Unfavorite Group DM`);
			}
			return i18n._(msg`Unfavorite Channel`);
		})();

		const favoriteItems: Array<MenuItemType> = [];
		if (favoriteEntry) {
			favoriteItems.push({
				icon: <FavoriteIcon size={20} filled={true} />,
				label: unfavoriteLabel,
				onClick: handlers.handleRemoveFromFavorites,
			});
		} else if (favoritesCategories.length === 0) {
			favoriteItems.push({
				icon: <FavoriteIcon size={20} filled={true} />,
				label: favoriteLabel,
				onClick: () => handlers.handleAddToFavorites(null),
			});
		} else {
			favoriteItems.push({
				icon: <FavoriteIcon size={20} filled={true} />,
				label: favoriteLabel,
				onClick: () => openNestedSheet(favoriteLabel, buildFavoriteGroups(handlers)),
			});
		}
		if (favoriteItems.length > 0) {
			mobileMenuGroups.push({items: favoriteItems});
		}
	}

	if (recipient && !recipient.bot) {
		const profileItems: Array<MenuItemType> = [
			{
				icon: <ViewProfileIcon size={20} />,
				label: i18n._(msg`View Profile`),
				onClick: handlers.handleViewProfile,
			},
			{
				icon: <VoiceCallIcon size={20} />,
				label: i18n._(msg`Start Voice Call`),
				onClick: handlers.handleStartVoiceCall,
			},
		];
		if (!restrictRecipientActions) {
			profileItems.push(
				{
					icon: <AddNoteIcon size={20} />,
					label: i18n._(msg`Add Note`),
					onClick: handlers.handleAddNote,
				},
				relationshipType === RelationshipTypes.FRIEND
					? {
							icon: <EditIcon size={20} />,
							label: i18n._(msg`Change Friend Nickname`),
							onClick: handlers.handleChangeFriendNickname,
						}
					: {
							icon: <EditIcon size={20} />,
							label: i18n._(msg`Change Friend Nickname`),
							onClick: handlers.handleChangeFriendNickname,
							disabled: true,
						},
			);
		}
		profileItems.push({
			icon: <CloseDMIcon size={20} />,
			label: i18n._(msg`Close DM`),
			onClick: handlers.handleCloseDm,
			danger: true,
		});
		mobileMenuGroups.push({items: profileItems});
	}

	const relationshipItems: Array<MenuItemType> = [];

	if (recipient && !restrictRecipientActions) {
		if (inviteCandidates.length > 0) {
			relationshipItems.push({
				icon: <InviteToCommunityIcon size={20} />,
				label: i18n._(msg`Invite to Community`),
				onClick: () => openNestedSheet(i18n._(msg`Invite to Community`), buildInviteGroups(inviteCandidates, handlers)),
			});
		}

		if (recipient.bot && !(recipient.flags & PublicUserFlags.FRIENDLY_BOT)) {
			if (relationshipType === RelationshipTypes.FRIEND) {
				relationshipItems.push({
					icon: <RemoveFriendIcon size={20} />,
					label: i18n._(msg`Remove Friend`),
					onClick: handlers.handleRemoveFriend,
				});
			} else if (relationshipType === RelationshipTypes.INCOMING_REQUEST) {
				relationshipItems.push(
					{
						icon: <AcceptFriendRequestIcon size={20} />,
						label: i18n._(msg`Accept Friend Request`),
						onClick: handlers.handleAcceptFriendRequest,
					},
					{
						icon: <IgnoreFriendRequestIcon size={20} />,
						label: i18n._(msg`Ignore Friend Request`),
						onClick: handlers.handleIgnoreFriendRequest,
					},
				);
			} else if (relationshipType === RelationshipTypes.OUTGOING_REQUEST) {
				relationshipItems.push({
					icon: <CancelFriendRequestIcon size={20} />,
					label: i18n._(msg`Friend Request Sent`),
					onClick: () => undefined,
					disabled: true,
				});
			} else if (UserStore.currentUser?.verified !== false) {
				relationshipItems.push({
					icon: <SendFriendRequestIcon size={20} />,
					label: i18n._(msg`Add Friend`),
					onClick: handlers.handleSendFriendRequest,
				});
			}
		} else {
			switch (relationshipType) {
				case RelationshipTypes.FRIEND:
					relationshipItems.push({
						icon: <RemoveFriendIcon size={20} />,
						label: i18n._(msg`Remove Friend`),
						onClick: handlers.handleRemoveFriend,
					});
					break;
				case RelationshipTypes.INCOMING_REQUEST:
					relationshipItems.push(
						{
							icon: <AcceptFriendRequestIcon size={20} />,
							label: i18n._(msg`Accept Friend Request`),
							onClick: handlers.handleAcceptFriendRequest,
						},
						{
							icon: <IgnoreFriendRequestIcon size={20} />,
							label: i18n._(msg`Ignore Friend Request`),
							onClick: handlers.handleIgnoreFriendRequest,
						},
					);
					break;
				case RelationshipTypes.OUTGOING_REQUEST:
					relationshipItems.push({
						icon: <CancelFriendRequestIcon size={20} />,
						label: i18n._(msg`Friend Request Sent`),
						onClick: () => undefined,
						disabled: true,
					});
					break;
				default:
					if (UserStore.currentUser?.verified !== false) {
						relationshipItems.push({
							icon: <SendFriendRequestIcon size={20} />,
							label: i18n._(msg`Add Friend`),
							onClick: handlers.handleSendFriendRequest,
						});
					}
			}
		}

		if (relationshipType === RelationshipTypes.BLOCKED) {
			relationshipItems.push({
				icon: <BlockUserIcon size={20} />,
				label: i18n._(msg`Unblock`),
				onClick: handlers.handleUnblockUser,
			});
		} else {
			relationshipItems.push({
				icon: <BlockUserIcon size={20} />,
				label: i18n._(msg`Block`),
				onClick: handlers.handleBlockUser,
			});
		}
	}

	if (relationshipItems.length > 0) {
		mobileMenuGroups.push({items: relationshipItems});
	}

	const channelOverride = UserGuildSettingsStore.getChannelOverride(null, channel.id);
	const bottomSheetIsMuted = channelOverride?.muted ?? false;
	const bottomSheetMutedHint = getMutedText(bottomSheetIsMuted, channelOverride?.mute_config);

	if (bottomSheetIsMuted) {
		mobileMenuGroups.push({
			items: [
				{
					icon: <MuteIcon size={20} />,
					label: i18n._(msg`Unmute DM`),
					onClick: handlers.handleUnmute,
					hint: bottomSheetMutedHint ?? undefined,
				},
			],
		});
	} else {
		mobileMenuGroups.push({
			items: [
				{
					icon: <MuteIcon size={20} />,
					label: i18n._(msg`Mute DM`),
					onClick: () => openNestedSheet(i18n._(msg`Mute DM`), buildMuteGroups(i18n, handlers)),
					hint: bottomSheetMutedHint ?? undefined,
				},
			],
		});
	}

	if (UserSettingsStore.developerMode) {
		const developerItems: Array<MenuItemType> = [];
		if (recipient) {
			developerItems.push({
				icon: <DebugIcon size={20} />,
				label: i18n._(msg`Debug User`),
				onClick: () => {
					closeAllSheets();
					ModalActionCreators.push(modal(() => <UserDebugModal title={i18n._(msg`User Debug`)} user={recipient} />));
				},
			});
		}
		developerItems.push({
			icon: <DebugIcon size={20} />,
			label: i18n._(msg`Debug Channel`),
			onClick: () => {
				closeAllSheets();
				ModalActionCreators.push(
					modal(() => <ChannelDebugModal title={i18n._(msg`Channel Debug`)} channel={channel} />),
				);
			},
		});
		mobileMenuGroups.push({items: developerItems});
	}

	const copyItems: Array<MenuItemType> = [];
	if (recipient) {
		copyItems.push({
			icon: <CopyIdIcon size={20} />,
			label: i18n._(msg`Copy User ID`),
			onClick: handlers.handleCopyUserId,
		});
	}
	copyItems.push({
		icon: <CopyIdIcon size={20} />,
		label: i18n._(msg`Copy Channel ID`),
		onClick: handlers.handleCopyChannelId,
	});
	mobileMenuGroups.push({items: copyItems});

	return mobileMenuGroups;
}
